// ABOUTME: Settings view where the warehouse fills in the scorecard: the points every activity earns in the day totals.
// ABOUTME: All values live in one setting (key "scorecard", objectBody) and are saved together with one Save button.

import { Box, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../../../../theme";
import Text from "../../../../../../../components/theme/text/Text";
import Subheading from "../../../../../../../components/theme/text/Subheading";
import NumberInput from "../../../../../../../components/theme/inputs/NumberInput";
import SettingsSaveButton from "../../../../../../../components/global/Settings/SettingsSaveButton";
import { useGetSettingsQuery, useUpdateSettingMutation } from "../../../../../../../newapi/global/generalApiSlice";
import { useGetWarehouseLocationTypesSelectQuery } from "../../../../../../../newapi/warehouse/warehouseLocationTypeSlice";
import { showToastMessage, showToastMessageRequestError } from "../../../../../../../utils/toasts";

const SCORECARD_KEY = "scorecard";

// What a new scorecard starts with. A max of null means every extra counts.
const defaultScorecard = {
  pick: { location: 1, extraProduct: 0.1, extraProductMax: 20, extraPiece: 0, extraPieceMax: null },
  ship: { order: 1, extraLine: 0.1, extraLineMax: null },
  return: { return: 1, extraLine: 0.1, extraLineMax: null },
  receive: { line: 0.5, repeat: 0.5 },
  putaway: { location: 1, extraProduct: 0.1, extraProductMax: null },
  locationTypes: {},
};

// Fields that hold a maximum; these may stay empty, every other field falls back to 0
const maxFields = ["extraProductMax", "extraPieceMax", "extraLineMax"];

// A stored scorecard on top of the defaults, so a field added later still has a value
const withDefaults = (stored) => ({
  pick: { ...defaultScorecard.pick, ...stored?.pick },
  ship: { ...defaultScorecard.ship, ...stored?.ship },
  return: { ...defaultScorecard.return, ...stored?.return },
  receive: { ...defaultScorecard.receive, ...stored?.receive },
  putaway: { ...defaultScorecard.putaway, ...stored?.putaway },
  locationTypes: { ...stored?.locationTypes },
});

// The scorecard as it is stored: numbers everywhere, null for an empty max
const normalize = (scorecard) => {
  const normalized = { locationTypes: {} };

  for (const group of ["pick", "ship", "return", "receive", "putaway"]) {
    normalized[group] = {};
    for (const [field, value] of Object.entries(scorecard[group])) {
      const isEmpty = value === "" || value === null || typeof value === "undefined";
      if (maxFields.includes(field)) {
        normalized[group][field] = isEmpty ? null : Number(value);
      } else {
        normalized[group][field] = isEmpty ? 0 : Number(value);
      }
    }
  }

  for (const [locationTypeId, percentage] of Object.entries(scorecard.locationTypes)) {
    if (percentage !== "" && percentage !== null && Number(percentage) !== 0) {
      normalized.locationTypes[locationTypeId] = Number(percentage);
    }
  }

  return normalized;
};

const ScoreRow = ({ label, value, onChange, onlyPositive = true }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Text light>{label}</Text>
      <Box
        sx={{
          width: 160,
          flexShrink: 0,
          backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
          borderRadius: 4,
          display: "flex",
          height: 32,
        }}
      >
        <NumberInput
          hideButtons
          onlyPositive={onlyPositive}
          value={value ?? ""}
          change={onChange}
        />
      </Box>
    </Box>
  );
};

const ScoreGroup = ({ title, subtitle, children }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 1,
    }}
  >
    <Box>
      <Text semibold>{title}</Text>
      {subtitle && <Box><Text light>{subtitle}</Text></Box>}
    </Box>
    {children}
  </Box>
);

const Scorecard = (props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { data: settings } = useGetSettingsQuery(SCORECARD_KEY);
  const { data: locationTypes } = useGetWarehouseLocationTypesSelectQuery();
  const [updateSetting, { isLoading: isSaving }] = useUpdateSettingMutation();

  const storedSetting = useMemo(() => settings?.find((setting) => setting.key === SCORECARD_KEY), [settings]);
  const [scorecard, setScorecard] = useState(withDefaults(null));

  useEffect(() => {
    props.replaceView({
      ...props.config,
    });

    return () => {};
  }, []);

  useEffect(() => {
    if (typeof settings !== "undefined") {
      setScorecard(withDefaults(storedSetting?.objectBody));
    }
  }, [settings, storedSetting]);

  // Until the scorecard is saved once no points are counted, so the defaults can be saved as they are
  const hasChanges = !storedSetting || JSON.stringify(normalize(scorecard)) !== JSON.stringify(normalize(withDefaults(storedSetting.objectBody)));

  const handleChange = (group, field, value) => {
    setScorecard((prev) => ({
      ...prev,
      [group]: { ...prev[group], [field]: value },
    }));
  };

  const save = async () => {
    if (isSaving) return;
    try {
      await updateSetting({ key: SCORECARD_KEY, warehouseId: -1, value: "", objectBody: normalize(scorecard) }).unwrap();
      showToastMessage("success", t("update_successfull"));
    } catch (error) {
      showToastMessageRequestError(t("update_failed"), t, error);
    }
  };

  return props.isActive ? (
    <Box
      sx={{
        height: 1,
        "@media screen and (max-width: 47.9375em)": {
          paddingTop: 0,
          paddingX: 0,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: 1,
          gap: 4,
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? colors.grey[300]
              : colors.bg["tertiary"],
          paddingX: 2,
          paddingTop: 4,
          height: 1,
          position: "relative",
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          "@media screen and (max-width: 47.9375em)": {
            paddingTop: 0,
            paddingX: 0,
            background: colors.bg["tertiary"],
          },
        }}
      >
        <Box
          sx={{
            paddingX: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            sx={{
              "@media screen and (max-width: 47.9375em)": {
                display: "none",
              },
            }}
          >
            <Subheading>{t("core_scorecard_heading")}</Subheading>
            <Text light>{t("core_scorecard_subtitle")}</Text>
          </Box>
          <SettingsSaveButton onClick={save} isLoading={isSaving} disabled={!hasChanges} />
        </Box>
        <Box
          sx={{
            background:
              theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
            flex: 1,
            overflow: "auto",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            "@media screen and (max-width: 48em)": {
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[200]
                  : colors.grey[0],
            },
          }}
        >
          <Box
            sx={{
              padding: 3,
              display: "flex",
              flexDirection: "column",
              gap: 4,
              maxWidth: 720,
            }}
          >
            {typeof settings !== "undefined" && !storedSetting && (
              <Text bold>{t("scorecard_not_saved_yet")}</Text>
            )}

            <ScoreGroup title={t("scorecard_pick_title")} subtitle={t("scorecard_pick_subtitle")}>
              <ScoreRow label={t("scorecard_points_per_location")} value={scorecard.pick.location} onChange={(value) => handleChange("pick", "location", value)} />
              <ScoreRow label={t("scorecard_points_per_extra_product")} value={scorecard.pick.extraProduct} onChange={(value) => handleChange("pick", "extraProduct", value)} />
              <ScoreRow label={t("scorecard_max_extra_products")} value={scorecard.pick.extraProductMax} onChange={(value) => handleChange("pick", "extraProductMax", value)} />
              <ScoreRow label={t("scorecard_points_per_extra_piece")} value={scorecard.pick.extraPiece} onChange={(value) => handleChange("pick", "extraPiece", value)} />
              <ScoreRow label={t("scorecard_max_extra_pieces")} value={scorecard.pick.extraPieceMax} onChange={(value) => handleChange("pick", "extraPieceMax", value)} />
            </ScoreGroup>

            <ScoreGroup title={t("scorecard_ship_title")} subtitle={t("scorecard_ship_subtitle")}>
              <ScoreRow label={t("scorecard_points_per_order")} value={scorecard.ship.order} onChange={(value) => handleChange("ship", "order", value)} />
              <ScoreRow label={t("scorecard_points_per_extra_orderline")} value={scorecard.ship.extraLine} onChange={(value) => handleChange("ship", "extraLine", value)} />
              <ScoreRow label={t("scorecard_max_extra_orderlines")} value={scorecard.ship.extraLineMax} onChange={(value) => handleChange("ship", "extraLineMax", value)} />
            </ScoreGroup>

            <ScoreGroup title={t("scorecard_return_title")} subtitle={t("scorecard_return_subtitle")}>
              <ScoreRow label={t("scorecard_points_per_return")} value={scorecard.return.return} onChange={(value) => handleChange("return", "return", value)} />
              <ScoreRow label={t("scorecard_points_per_extra_returnline")} value={scorecard.return.extraLine} onChange={(value) => handleChange("return", "extraLine", value)} />
              <ScoreRow label={t("scorecard_max_extra_returnlines")} value={scorecard.return.extraLineMax} onChange={(value) => handleChange("return", "extraLineMax", value)} />
            </ScoreGroup>

            <ScoreGroup title={t("scorecard_receive_title")} subtitle={t("scorecard_receive_subtitle")}>
              <ScoreRow label={t("scorecard_points_per_received_line")} value={scorecard.receive.line} onChange={(value) => handleChange("receive", "line", value)} />
              <ScoreRow label={t("scorecard_points_per_repeat_booking")} value={scorecard.receive.repeat} onChange={(value) => handleChange("receive", "repeat", value)} />
            </ScoreGroup>

            <ScoreGroup title={t("scorecard_putaway_title")} subtitle={t("scorecard_putaway_subtitle")}>
              <ScoreRow label={t("scorecard_points_per_location")} value={scorecard.putaway.location} onChange={(value) => handleChange("putaway", "location", value)} />
              <ScoreRow label={t("scorecard_points_per_extra_product")} value={scorecard.putaway.extraProduct} onChange={(value) => handleChange("putaway", "extraProduct", value)} />
              <ScoreRow label={t("scorecard_max_extra_products")} value={scorecard.putaway.extraProductMax} onChange={(value) => handleChange("putaway", "extraProductMax", value)} />
            </ScoreGroup>

            <ScoreGroup title={t("scorecard_location_types_title")} subtitle={t("scorecard_location_types_subtitle")}>
              {locationTypes?.data?.map((locationType) => (
                <ScoreRow
                  key={"scorecard-locationtype-" + locationType.id}
                  label={locationType.description}
                  onlyPositive={false}
                  value={scorecard.locationTypes[locationType.id]}
                  onChange={(value) => handleChange("locationTypes", locationType.id, value)}
                />
              ))}
            </ScoreGroup>
          </Box>
        </Box>
      </Box>
    </Box>
  ) : null;
};

export default Scorecard;
